import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { MilestonesGridData, MomentData } from '../types';
import { selectSortSelections } from '../slice/selectors';

/**
 * Parse date string for sorting - handles multiple date formats
 * Supports: ISO dates (YYYY-MM-DD), Quarters (Q1 2025), Months (Sep 2025)
 * Returns a timestamp with encoded granularity for proper precedence:
 * - Quarters (least specific) sort first: timestamp + 0ms
 * - Months (medium specific) sort second: timestamp + 1ms
 * - Exact dates (most specific) sort third: timestamp + 2ms
 */
const parseDateForSort = (dateStr: string | undefined): number => {
  // Handle empty, undefined, null, N/A, or TBD
  if (!dateStr || dateStr === '' || dateStr === 'N/A' || dateStr === 'TBD') {
    return Infinity; // Put N/A, TBD, and empty at the end
  }

  const trimmedDate = dateStr.trim();
  if (trimmedDate === '') {
    return Infinity;
  }

  // Granularity offsets (in milliseconds)
  const GRANULARITY_OFFSET = {
    QUARTER: 0, // Broadest - comes first
    MONTH: 1, // Medium - comes second
    DATE: 2 // Most specific - comes third
  };

  // Handle ISO date format (YYYY-MM-DD) - Most specific
  // IMPORTANT: Parse ISO date WITHOUT timezone conversion to avoid date shifts
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmedDate)) {
    const [year, month, day] = trimmedDate.split('-').map(Number);
    // Use Date constructor with year, month (0-indexed), day to avoid timezone issues
    return new Date(year, month - 1, day).getTime() + GRANULARITY_OFFSET.DATE;
  }

  // Handle quarter format (Q1 2025, Q2 2024, etc.)
  // Fiscal year quarters: Q1=Dec-Feb, Q2=Mar-May, Q3=Jun-Aug, Q4=Sep-Nov
  const quarterMatch = trimmedDate.match(/^Q([1-4])\s+(\d{4})$/i);
  if (quarterMatch) {
    const quarter = parseInt(quarterMatch[1], 10);
    const year = parseInt(quarterMatch[2], 10);

    let month: number;
    let actualYear: number;

    if (quarter === 1) {
      // Q1 starts in December of the previous year
      month = 11; // December
      actualYear = year - 1;
    } else if (quarter === 2) {
      // Q2 starts in March
      month = 2; // March
      actualYear = year;
    } else if (quarter === 3) {
      // Q3 starts in June
      month = 5; // June
      actualYear = year;
    } else {
      // Q4 starts in September
      month = 8; // September
      actualYear = year;
    }

    return (
      new Date(actualYear, month, 1).getTime() + GRANULARITY_OFFSET.QUARTER
    );
  }

  // Handle month format (Sep 2025, Jan 2024, etc.) - Medium specificity
  const monthMatch = trimmedDate.match(/^([A-Za-z]{3})\s+(\d{4})$/);
  if (monthMatch) {
    const monthStr = monthMatch[1];
    const year = parseInt(monthMatch[2], 10);
    const monthMap: { [key: string]: number } = {
      jan: 0,
      feb: 1,
      mar: 2,
      apr: 3,
      may: 4,
      jun: 5,
      jul: 6,
      aug: 7,
      sep: 8,
      oct: 9,
      nov: 10,
      dec: 11
    };
    const month = monthMap[monthStr.toLowerCase()];
    if (month !== undefined) {
      return new Date(year, month, 1).getTime() + GRANULARITY_OFFSET.MONTH;
    }
  }

  // Handle month without year (Sep, Jan, etc.) - assume current year
  const monthOnlyMatch = trimmedDate.match(/^([A-Za-z]{3})$/i);
  if (monthOnlyMatch) {
    const monthStr = monthOnlyMatch[1];
    const currentYear = new Date().getFullYear();
    const monthMap: { [key: string]: number } = {
      jan: 0,
      feb: 1,
      mar: 2,
      apr: 3,
      may: 4,
      jun: 5,
      jul: 6,
      aug: 7,
      sep: 8,
      oct: 9,
      nov: 10,
      dec: 11
    };
    const month = monthMap[monthStr.toLowerCase()];
    if (month !== undefined) {
      return (
        new Date(currentYear, month, 1).getTime() + GRANULARITY_OFFSET.MONTH
      );
    }
  }

  return Infinity; // Unrecognized format goes to the end
};

/**
 * Hook to sort milestones by DATE column
 * Supports:
 * - Date sorting (Ascending, Descending)
 * - Handles multiple date formats: ISO dates, Quarters, Months
 */
export const useSortByDate = (
  data: MilestonesGridData | null
): MilestonesGridData | null => {
  const sortSelections = useSelector(selectSortSelections);

  return useMemo(() => {
    if (!data || !data.data || data.data.length === 0) {
      return data;
    }

    // Check if DATE column has the active shared sort
    const isDateColumnActive = sortSelections.sharedSortColumn === 'date';
    const sortOption = isDateColumnActive
      ? sortSelections.sharedSort
      : undefined;

    // If no sort option for this column, return data as-is
    if (!sortOption) {
      return data;
    }

    // Clone data to avoid mutation
    const sortedData = { ...data, data: [...data.data] };

    sortedData.data.sort((a: MomentData, b: MomentData) => {
      const dateA = a.metaDetails?.date;
      const dateB = b.metaDetails?.date;

      // Parse dates using the robust parser
      const timeA = parseDateForSort(dateA);
      const timeB = parseDateForSort(dateB);

      if (sortOption === 'ascending') {
        // Earliest dates first, N/A and TBD at the end
        return timeA - timeB;
      } else if (sortOption === 'descending') {
        // Latest dates first, N/A and TBD at the end
        return timeB - timeA;
      }

      return 0;
    });

    return sortedData;
  }, [data, sortSelections.sharedSort, sortSelections.sharedSortColumn]);
};
